import React, { useState, useEffect } from 'react';
import { getInbox, markInboxRead, deleteInboxEmail } from '../services/settingsApi';

function InboxPage() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    loadInbox();
  }, []);

  const loadInbox = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getInbox();
      setEmails(res.emails || []);
      setUnreadCount(res.unread || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = async (email) => {
    if (expandedId === email.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(email.id);
    if (!email.read) {
      try {
        await markInboxRead(email.id);
        setEmails(prev => prev.map(e =>
          e.id === email.id ? { ...e, read: true } : e
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Failed to mark as read:', err);
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      setDeletingId(id);
      await deleteInboxEmail(id);
      setEmails(prev => prev.filter(e => e.id !== id));
      if (expandedId === id) setExpandedId(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 1) return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;
    if (diffHours < 48) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  const styles = {
    container: {
      padding: 'var(--space-6)',
      maxWidth: '800px',
      margin: '0 auto',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 'var(--space-6)',
    },
    title: {
      fontSize: 'var(--font-size-2xl)',
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--color-text-primary)',
      letterSpacing: 'var(--letter-spacing-tight)',
    },
    badge: {
      backgroundColor: 'var(--color-primary)',
      color: '#fff',
      fontSize: 'var(--font-size-xs)',
      fontWeight: 'var(--font-weight-semibold)',
      padding: '2px 8px',
      borderRadius: '12px',
      marginLeft: 'var(--space-2)',
    },
    section: {
      backgroundColor: 'var(--color-bg-base)',
      borderRadius: 'var(--radius-xl)',
      boxShadow: 'var(--shadow-card)',
      border: '1px solid var(--color-border-light)',
      overflow: 'hidden',
    },
    emailItem: {
      padding: 'var(--space-4)',
      borderBottom: '1px solid var(--color-border-light)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
    },
    emailHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 'var(--space-3)',
    },
    from: {
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-text-primary)',
    },
    subject: {
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-text-secondary)',
      marginTop: '2px',
    },
    date: {
      fontSize: 'var(--font-size-xs)',
      color: 'var(--color-text-tertiary)',
      whiteSpace: 'nowrap',
    },
    body: {
      marginTop: 'var(--space-3)',
      padding: 'var(--space-4)',
      backgroundColor: 'var(--color-bg-subtle, #f9fafb)',
      borderRadius: 'var(--radius-default)',
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-text-primary)',
      lineHeight: '1.6',
      overflowWrap: 'break-word',
      maxHeight: '400px',
      overflowY: 'auto',
    },
    deleteBtn: {
      padding: '4px 10px',
      borderRadius: 'var(--radius-default)',
      border: '1px solid var(--color-border-default)',
      backgroundColor: 'transparent',
      color: 'var(--color-text-tertiary)',
      fontSize: 'var(--font-size-xs)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
    },
    empty: {
      padding: 'var(--space-8)',
      textAlign: 'center',
      color: 'var(--color-text-tertiary)',
      fontSize: 'var(--font-size-sm)',
    },
    refreshBtn: {
      padding: 'var(--space-2) var(--space-4)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--color-border-default)',
      backgroundColor: 'var(--color-bg-base)',
      color: 'var(--color-text-primary)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
    },
    errorBox: {
      padding: 'var(--space-3)',
      backgroundColor: 'var(--color-error-light)',
      border: '1px solid var(--color-error)',
      borderRadius: 'var(--radius-default)',
      marginBottom: 'var(--space-4)',
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-error-dark)',
    },
    info: {
      padding: 'var(--space-3)',
      backgroundColor: 'var(--color-info-light)',
      border: '1px solid var(--color-border-default)',
      borderRadius: 'var(--radius-default)',
      marginBottom: 'var(--space-4)',
      fontSize: 'var(--font-size-xs)',
      color: 'var(--color-info)',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.title}>Inbox</div>
        <div style={styles.section}>
          <div style={styles.empty}>Loading emails...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h2 style={styles.title}>Inbox</h2>
          {unreadCount > 0 && <span style={styles.badge}>{unreadCount}</span>}
        </div>
        <button
          onClick={loadInbox}
          style={styles.refreshBtn}
          onMouseEnter={(e) => { e.target.style.backgroundColor = 'var(--color-bg-muted)'; }}
          onMouseLeave={(e) => { e.target.style.backgroundColor = 'var(--color-bg-base)'; }}
        >
          Refresh
        </button>
      </div>

      <div style={styles.info}>
        Emails sent to <strong>feedback@inbound.zenithexpense.xyz</strong> will appear here.
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.section}>
        {emails.length === 0 ? (
          <div style={styles.empty}>No emails yet</div>
        ) : (
          emails.map((email) => (
            <div
              key={email.id}
              style={{
                ...styles.emailItem,
                backgroundColor: email.read ? 'transparent' : 'var(--color-info-light, #eff6ff)',
              }}
            >
              <div
                onClick={() => handleExpand(email)}
                style={styles.emailHeader}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    ...styles.from,
                    fontWeight: email.read ? 'var(--font-weight-medium)' : 'var(--font-weight-semibold)',
                  }}>
                    {email.from_address}
                  </div>
                  <div style={{
                    ...styles.subject,
                    fontWeight: email.read ? 'normal' : 'var(--font-weight-medium)',
                  }}>
                    {email.subject}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <span style={styles.date}>{formatDate(email.created_at)}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(email.id); }}
                    disabled={deletingId === email.id}
                    style={{
                      ...styles.deleteBtn,
                      opacity: deletingId === email.id ? 0.5 : 1,
                    }}
                    onMouseEnter={(e) => { e.target.style.borderColor = '#dc2626'; e.target.style.color = '#dc2626'; }}
                    onMouseLeave={(e) => { e.target.style.borderColor = 'var(--color-border-default)'; e.target.style.color = 'var(--color-text-tertiary)'; }}
                  >
                    {deletingId === email.id ? '...' : 'Delete'}
                  </button>
                </div>
              </div>

              {expandedId === email.id && email.body && (
                <div
                  style={styles.body}
                  dangerouslySetInnerHTML={{ __html: email.body }}
                />
              )}
              {expandedId === email.id && !email.body && (
                <div style={{ ...styles.body, fontStyle: 'italic', color: 'var(--color-text-tertiary)' }}>
                  No email body available
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default InboxPage;
